import React from "react";
import { Card } from "antd";

import "./VisualLedger.css";

export type VisualLedgerTone =
  "neutral" | "muted" | "success" | "warning" | "danger" | "info";

export function VisualLedgerPageHeader({
  title,
  subtitle,
  actions,
  className = "",
  testId,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <header
      className={`vf-ledger-page-header ${className}`.trim()}
      data-testid={testId}
    >
      <div className="vf-ledger-page-header__copy">
        <h1 className="vf-ledger-page-header__title">{title}</h1>
        {subtitle ? (
          <p className="vf-ledger-page-header__subtitle">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div className="vf-ledger-page-header__actions">{actions}</div>
      ) : null}
    </header>
  );
}

type VisualLedgerPanelProps = {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  loading?: boolean;
  testId?: string;
};

export function VisualLedgerPanel({
  title,
  subtitle,
  extra,
  children,
  className = "",
  bodyClassName = "",
  loading = false,
  testId,
}: VisualLedgerPanelProps) {
  return (
    <Card
      className={`vf-ledger-panel ${className}`.trim()}
      title={
        <span className="vf-ledger-panel__heading">
          <span className="vf-ledger-panel__title">{title}</span>
          {subtitle ? (
            <span className="vf-ledger-panel__subtitle">{subtitle}</span>
          ) : null}
        </span>
      }
      extra={extra}
      classNames={{ body: bodyClassName }}
      loading={loading}
      data-testid={testId}
    >
      {children}
    </Card>
  );
}

export function VisualLedgerKpiGrid({
  children,
  label,
  columns = 5,
  size = "default",
}: {
  children: React.ReactNode;
  label: string;
  columns?: 2 | 3 | 4 | 5 | 6;
  size?: "default" | "compact";
}) {
  return (
    <section
      className={`vf-ledger-kpis vf-ledger-kpis--columns-${columns} vf-ledger-kpis--${size}`}
      aria-label={label}
    >
      {children}
    </section>
  );
}

export function VisualLedgerKpi({
  label,
  value,
  hint,
  visual,
  tone = "neutral",
  className = "",
  interactive = false,
  onClick,
  disabled = false,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  visual?: React.ReactNode;
  tone?: VisualLedgerTone;
  className?: string;
  interactive?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
}) {
  const content = (
    <>
      <span className="vf-ledger-kpi__label">{label}</span>
      <div className="vf-ledger-kpi__main">
        <span className="vf-ledger-kpi__value">{value}</span>
        {visual ? (
          <span className="vf-ledger-kpi__visual">{visual}</span>
        ) : null}
      </div>
      {hint ? <span className="vf-ledger-kpi__hint">{hint}</span> : null}
    </>
  );
  const classes = `vf-ledger-kpi vf-ledger-kpi--${tone}${
    interactive ? " vf-ledger-kpi--interactive" : ""
  } ${className}`.trim();

  return interactive ? (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  ) : (
    <div className={classes}>{content}</div>
  );
}

export function VisualLedgerPacingBar({
  value,
  marker,
  tone = "neutral",
  fillColor,
  label,
}: {
  value: number | null;
  marker?: number | null;
  tone?: VisualLedgerTone;
  fillColor?: string;
  label: string;
}) {
  const width = clampPercent(value);
  const markerLeft =
    marker === null || marker === undefined ? null : clampPercent(marker);
  return (
    <span className="vf-ledger-pacing" role="img" aria-label={label}>
      <span
        className={`vf-ledger-pacing__fill vf-ledger-pacing__fill--${tone}`}
        style={{ width: `${width}%`, backgroundColor: fillColor }}
      />
      {markerLeft === null ? null : (
        <span
          className="vf-ledger-pacing__marker"
          style={{ left: `${markerLeft}%` }}
        />
      )}
    </span>
  );
}

export function VisualLedgerEntityDot({ color }: { color: string }) {
  return (
    <span
      className="vf-ledger-entity-dot"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    />
  );
}

export function VisualLedgerStatusPill({
  children,
  tone = "neutral",
  testId,
}: {
  children: React.ReactNode;
  tone?: VisualLedgerTone;
  testId?: string;
}) {
  return (
    <span
      className={`vf-ledger-status vf-ledger-status--${tone}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

export type VisualLedgerDefItem = {
  label: React.ReactNode;
  value: React.ReactNode;
  minor?: boolean;
  control?: React.ReactNode;
};

export function VisualLedgerDefList({
  items,
  labelWidth,
  className = "",
}: {
  items: VisualLedgerDefItem[];
  labelWidth?: number | string;
  className?: string;
}) {
  return (
    <dl
      className={`vf-ledger-def-list ${className}`.trim()}
      style={
        labelWidth === undefined
          ? undefined
          : ({
              "--vf-ledger-def-label-width": labelWidth,
            } as React.CSSProperties)
      }
    >
      {items.map((item, index) => (
        <div className="vf-ledger-def-list__row" key={index}>
          <dt className="vf-ledger-def-list__label">{item.label}</dt>
          <dd
            className={`vf-ledger-def-list__value${
              item.minor ? " vf-ledger-def-list__value--minor" : ""
            }`}
          >
            <span className="vf-ledger-def-list__content">{item.value}</span>
            {item.control ? (
              <span className="vf-ledger-def-list__control">
                {item.control}
              </span>
            ) : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function clampPercent(value: number | null): number {
  if (value === null || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}
